import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Users, Search, ChevronDown, ChevronUp, Loader2, Shield, Phone, Download, Unlock, Mail, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function PaymentHistory({ payments, t, lang }) {
  if (!payments.length) return <p className="text-xs text-slate-500 mt-3" data-testid="user-no-payments">{t.admin.noPayments}</p>;
  return (
    <div className="mt-3 space-y-2" data-testid="user-payment-history">
      {payments.map((p) => (
        <div key={p.order_id} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="min-w-0">
            <div className="text-slate-200 font-mono truncate">{p.order_id}</div>
            <div className="text-slate-500">{fmtDate(p.paid_at, lang)} · {p.payment_id}{p.coupon ? ` · ${p.coupon} (${p.percent}%)` : ""}</div>
          </div>
          <div className={`font-serif text-base ${p.free ? "text-pink-300" : "text-emerald-300"}`}>₹{p.final_amount ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function ReadingActions({ readings, t, lang, onChanged }) {
  const [busy, setBusy] = useState(null);
  const act = async (r, kind) => {
    if (kind === "unlock" && !window.confirm(t.admin.confirmUnlock)) return;
    setBusy(`${kind}-${r.reading_id}`);
    try {
      const path = kind === "unlock" ? `/admin/readings/${r.reading_id}/unlock` : `/admin/readings/${r.reading_id}/resend-report`;
      const { data } = await api.post(path, { base_url: window.location.origin });
      toast.success(kind === "unlock" ? t.admin.unlockDone : data.mode === "pdf" ? t.admin.resentPdf : t.admin.resentLink);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };
  if (!readings.length) return null;
  return (
    <div className="mt-3" data-testid="user-readings">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">{t.admin.readingsTitle}</div>
      <div className="space-y-2">
        {readings.map((r) => (
          <div key={r.reading_id} data-testid={`admin-reading-${r.reading_id}`} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-slate-200">{r.reading_id}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${r.unlocked ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>{r.unlocked ? t.admin.unlockedBadge : t.admin.lockedBadge}</span>
                {r.unlocked && <span className={`px-1.5 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1 ${r.has_pdf ? "bg-sky-500/15 text-sky-300" : "bg-amber-500/10 text-amber-300/80"}`}><FileText size={10} /> {r.has_pdf ? t.admin.pdfStored : t.admin.noPdf}</span>}
              </div>
              <div className="text-slate-500 mt-0.5">{fmtDate(r.created_at, lang)} · {r.status}{r.seeker_name ? ` · ${r.seeker_name}` : ""}</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {!r.unlocked && r.status === "ready" && (
                <button onClick={() => act(r, "unlock")} disabled={!!busy} data-testid={`admin-unlock-${r.reading_id}`} className="rounded-full px-2.5 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25 inline-flex items-center gap-1 disabled:opacity-50">
                  {busy === `unlock-${r.reading_id}` ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />} {t.admin.freeUnlock}
                </button>
              )}
              {r.unlocked && (
                <button onClick={() => act(r, "resend")} disabled={!!busy} data-testid={`admin-resend-${r.reading_id}`} className="rounded-full px-2.5 py-1.5 bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 inline-flex items-center gap-1 disabled:opacity-50">
                  {busy === `resend-${r.reading_id}` ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {t.admin.resendPdf}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserRow({ u, t, lang, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-2xl p-4" data-testid={`user-row-${u.user_id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-purple-800/60 border border-amber-500/30 grid place-items-center text-amber-200 font-serif shrink-0 overflow-hidden">
          {u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : (u.name || u.email || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-amber-100 font-medium truncate">{u.name || "—"}</span>
            {u.is_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 inline-flex items-center gap-1"><Shield size={10} /> Admin</span>}
          </div>
          <div className="text-xs text-slate-400 truncate" data-testid="user-email">{u.email}</div>
          {u.display_name && u.display_name !== u.name && <div className="text-xs text-amber-200/80 truncate" data-testid="user-display-name">{u.display_name}</div>}
          <div className="text-xs mt-0.5 flex items-center gap-1" data-testid="user-mobile">
            <Phone size={11} className="text-emerald-300/80" />
            {u.mobile ? <a href={`tel:+91${u.mobile}`} className="text-emerald-200 hover:underline">+91 {u.mobile}</a> : <span className="text-slate-600">—</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{t.admin.registered}: {fmtDate(u.created_at, lang)}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid={`user-toggle-${u.user_id}`} className="shrink-0 h-8 w-8 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-300 hover:bg-white/10">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        {[
          { l: t.admin.readingsCol, v: u.readings_total, c: "text-sky-300" },
          { l: t.admin.paymentsCol, v: u.payments_count, c: "text-amber-300" },
          { l: t.admin.spentCol, v: `₹${u.total_spent}`, c: "text-emerald-300" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-white/5 py-2">
            <div className={`font-serif text-lg ${s.c}`}>{s.v}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{s.l}</div>
          </div>
        ))}
      </div>
      {open && (
        <>
          <ReadingActions readings={u.readings || []} t={t} lang={lang} onChanged={onChanged} />
          <PaymentHistory payments={u.payments} t={t} lang={lang} />
        </>
      )}
    </div>
  );
}

export const AdminUsers = () => {
  const { t, lang } = useApp();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/users.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "astrofriend_users_crm.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const list = (users || []).filter((u) => !q || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.display_name || "").toLowerCase().includes(q.toLowerCase()) || (u.mobile || "").includes(q));

  return (
    <section className="mt-10 sm:mt-12" data-testid="admin-users-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="text-amber-300" size={18} />
          <h2 className="text-lg text-amber-100 font-medium">{t.admin.usersTitle}</h2>
          {users && <span className="text-xs text-slate-400" data-testid="admin-users-count">({users.length})</span>}
        </div>
        <button data-testid="export-users-btn" onClick={exportCsv} className="btn-ghost-gold rounded-full px-4 py-2 flex items-center gap-2 text-xs sm:text-sm">
          <Download size={14} /> {t.admin.exportUsers}
        </button>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.admin.searchUsers}
            className="w-full rounded-full bg-white/5 border border-white/10 pl-9 pr-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400/50" />
        </div>
      </div>
      {!users ? (
        <div className="text-amber-200 py-6 grid place-items-center"><Loader2 className="animate-spin" /></div>
      ) : list.length === 0 ? (
        <p className="text-slate-500 text-sm">{t.admin.noUsers}</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((u) => <UserRow key={u.user_id} u={u} t={t} lang={lang} onChanged={load} />)}
        </div>
      )}
    </section>
  );
};
